import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Button, Form, Modal, OverlayTrigger, Tooltip } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import Select from 'react-select'
import {
  DocumentWorkflowDefinition,
  documentWorkflowDefinitionService,
  DocumentWorkflowDefinitionStepActionBranchWrite,
  DocumentWorkflowDefinitionStepActionWrite,
  DocumentWorkflowDefinitionStepEntryConditionWrite,
  DocumentWorkflowDefinitionStepWrite,
  DocumentWorkflowDefinitionWrite,
} from '../../services/document-workflow-definition.service'
import { documentTypeService } from '../../services/document-type.service'
import { DocumentWorkflowSupportedAction, documentWorkflowSupportedActionService } from '../../services/document-workflow-supported-action.service'
import { workflowStatusService } from '../../services/workflow-status.service'
import { DocumentType, WorkflowStatus } from '../../types'
import CustomFieldsQueryDropdown, {
  CustomFieldQueriesModel,
  CustomFieldQueryElement,
  CustomFieldQueryElementType,
} from '../common/CustomFieldsQueryDropdown'
import { InputPermissionsGroup } from '../common/InputPermissionsGroup'
import { InputWorkflowStepAssignees, WORKFLOW_DYNAMIC_RECIPIENT_SLUGS } from '../common/InputWorkflowStepAssignees'
import { DocumentWorkflowDefinitionVisualizationDialog } from './DocumentWorkflowDefinitionVisualizationDialog'
import './DocumentWorkflowDefinitionEditDialog.scss'

type NotificationDelivery = 'inherit' | 'websocket_only' | 'email_only' | 'websocket_and_email'

export type ActionOutcomeType = 'next_step' | 'back_to_step' | 'end_workflow'

interface BranchForm {
  condition_query: string
  conditionQueryModel: CustomFieldQueriesModel
  outcome_type: ActionOutcomeType
  target_step_index: number | null
  assign_workflow_status_id: number | ''
}

interface ActionForm {
  action_type: string
  name: string
  outcome_type: ActionOutcomeType
  target_step_index: number | null
  assign_workflow_status_id: number | ''
  comment_required: boolean
  is_conditional: boolean
  branches: BranchForm[]
}

interface EntryConditionForm {
  condition_query: string
  conditionQueryModel: CustomFieldQueriesModel
}

interface StepForm {
  workflow_status_id: number | ''
  name: string
  position: number
  assigned_user_ids: number[]
  assigned_group_ids: number[]
  assigned_dynamic_recipients: string[]
  notification_delivery: NotificationDelivery
  actions: ActionForm[]
  entry_conditions: EntryConditionForm[]
}

interface DocumentWorkflowDefinitionEditDialogProps {
  object?: DocumentWorkflowDefinition
  isCreating: boolean
  onSuccess: () => void
  onError: (error: any) => void
  onClose: () => void
}

const getDefaultLabelForActionType = (actionType: string, supportedActions: DocumentWorkflowSupportedAction[]): string =>
  actionType === 'custom' ? '' : (supportedActions.find((a) => a.slug === actionType)?.default_label ?? '')

const filterDynamicRecipients = (slugs: string[] | undefined): string[] =>
  (slugs ?? []).filter((slug): slug is (typeof WORKFLOW_DYNAMIC_RECIPIENT_SLUGS)[number] =>
    (WORKFLOW_DYNAMIC_RECIPIENT_SLUGS as readonly string[]).includes(slug),
  )

const emptyAction = (): ActionForm => ({
  action_type: 'custom',
  name: '',
  outcome_type: 'next_step',
  target_step_index: null,
  assign_workflow_status_id: '',
  comment_required: false,
  is_conditional: false,
  branches: [],
})

const emptyBranch = (): BranchForm => ({
  condition_query: '',
  conditionQueryModel: new CustomFieldQueriesModel(),
  outcome_type: 'next_step',
  target_step_index: null,
  assign_workflow_status_id: '',
})

const parseConditionQueryToModel = (conditionQuery: string): CustomFieldQueriesModel => {
  const model = new CustomFieldQueriesModel()
  if (!conditionQuery) return model
  try {
    const parsed = JSON.parse(conditionQuery)
    if (Array.isArray(parsed) && parsed.length === 2) {
      const [operator, elements] = parsed
      if (Array.isArray(elements)) {
        const mappedElements: CustomFieldQueryElement[] = []
        for (const el of elements) {
          if (Array.isArray(el) && el.length === 3) {
            mappedElements.push({
              type: CustomFieldQueryElementType.Atom,
              atom: { field: el[0], operator: el[1], value: el[2] },
            })
          } else if (Array.isArray(el) && el.length === 2) {
            const [op, elems] = el
            const nested: CustomFieldQueryElement[] = []
            for (const e of elems as unknown[]) {
              if (Array.isArray(e) && e.length === 3) {
                nested.push({
                  type: CustomFieldQueryElementType.Atom,
                  atom: { field: e[0], operator: e[1], value: e[2] },
                })
              }
            }
            mappedElements.push({
              type: CustomFieldQueryElementType.Expression,
              expression: { operator: op, elements: nested },
            })
          }
        }
        const expression: CustomFieldQueryElement = {
          type: CustomFieldQueryElementType.Expression,
          expression: {
            operator: operator as 'OR' | 'AND' | 'NOT',
            elements: mappedElements,
          },
        }
        model.queries = [expression]
      }
    }
  } catch {
    // ignore invalid stored query
  }
  return model
}

const emptyEntryCondition = (): EntryConditionForm => ({
  condition_query: '',
  conditionQueryModel: new CustomFieldQueriesModel(),
})

const emptyStep = (position: number, defaultWorkflowStatusId?: number): StepForm => ({
  workflow_status_id: defaultWorkflowStatusId ?? '',
  name: '',
  position,
  assigned_user_ids: [],
  assigned_group_ids: [],
  assigned_dynamic_recipients: [],
  notification_delivery: 'inherit',
  actions: [emptyAction()],
  entry_conditions: [],
})

function branchFromApi(
  b: {
    condition_query: string
    next_step_id?: number | null
    back_to_step_id?: number | null
    end_workflow: boolean
    assign_workflow_status_id?: number | null
  },
  stepsSorted: Array<{ id?: number }>,
): BranchForm {
  const assignWs: number | '' = b.assign_workflow_status_id != null ? b.assign_workflow_status_id : ''
  const model = parseConditionQueryToModel(b.condition_query || '')
  const base: BranchForm = {
    condition_query: b.condition_query || '',
    conditionQueryModel: model,
    outcome_type: 'next_step',
    target_step_index: null,
    assign_workflow_status_id: '',
  }
  if (b.end_workflow) {
    return { ...base, outcome_type: 'end_workflow', target_step_index: null, assign_workflow_status_id: assignWs }
  }
  if (b.next_step_id != null) {
    const idx = stepsSorted.findIndex((st) => st.id === b.next_step_id)
    return { ...base, outcome_type: 'next_step', target_step_index: idx >= 0 ? idx : null }
  }
  if (b.back_to_step_id != null) {
    const idx = stepsSorted.findIndex((st) => st.id === b.back_to_step_id)
    return { ...base, outcome_type: 'back_to_step', target_step_index: idx >= 0 ? idx : null }
  }
  return { ...base, outcome_type: 'end_workflow', assign_workflow_status_id: assignWs }
}

function actionFromApi(
  a: {
    name: string
    next_step_id?: number | null
    back_to_step_id?: number | null
    end_workflow: boolean
    assign_workflow_status_id?: number | null
    comment_required?: boolean
    branches?: Array<{
      position?: number
      condition_query: string
      next_step_id?: number | null
      back_to_step_id?: number | null
      end_workflow: boolean
      assign_workflow_status_id?: number | null
    }>
  },
  stepsSorted: Array<{ id?: number }>,
  supportedActions: DocumentWorkflowSupportedAction[]
): ActionForm {
  const name = a.name ?? ''
  const actionType = supportedActions.find((x) => x.default_label.toLowerCase() === name.trim().toLowerCase())?.slug ?? 'custom'
  const assignWs: number | '' = a.assign_workflow_status_id != null ? a.assign_workflow_status_id : ''
  const branches: BranchForm[] = [...(a.branches ?? [])]
    .sort((x, y) => (x.position ?? 0) - (y.position ?? 0))
    .map((b) => branchFromApi(b, stepsSorted))
  const base = {
    action_type: actionType,
    name,
    outcome_type: 'next_step' as ActionOutcomeType,
    target_step_index: null as number | null,
    assign_workflow_status_id: '' as number | '',
    comment_required: a.comment_required ?? false,
    is_conditional: branches.length > 0,
    branches,
  }
  if (a.end_workflow) {
    return { ...base, outcome_type: 'end_workflow', target_step_index: null, assign_workflow_status_id: assignWs }
  }
  if (a.next_step_id != null) {
    const idx = stepsSorted.findIndex((st) => st.id === a.next_step_id)
    return { ...base, outcome_type: 'next_step', target_step_index: idx >= 0 ? idx : null }
  }
  if (a.back_to_step_id != null) {
    const idx = stepsSorted.findIndex((st) => st.id === a.back_to_step_id)
    return { ...base, outcome_type: 'back_to_step', target_step_index: idx >= 0 ? idx : null }
  }
  return { ...base, outcome_type: 'end_workflow', target_step_index: null, assign_workflow_status_id: assignWs }
}

const InfoTip: React.FC<{ text: string }> = ({ text }) => (
  <OverlayTrigger placement="top" overlay={<Tooltip>{text}</Tooltip>}>
    <i
      className="bi bi-info-circle ms-1 text-muted"
      role="img"
      aria-label={text}
      style={{ cursor: 'help' }}
    />
  </OverlayTrigger>
)

const slugFromName = (name: string) =>
  name
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')

export const DocumentWorkflowDefinitionEditDialog: React.FC<DocumentWorkflowDefinitionEditDialogProps> = ({
  object,
  isCreating,
  onSuccess,
  onError,
  onClose,
}) => {
  const { t } = useTranslation()
  const [loading, setLoading] = useState(false)
  // Držimo spinner do prve inicijalizacije (uključujući učitavanje supportedActions).
  const [loadDetail, setLoadDetail] = useState(!!object?.id)
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [applicableDocumentTypeIds, setApplicableDocumentTypeIds] = useState<number[]>([])
  const [documentTypes, setDocumentTypes] = useState<DocumentType[]>([])
  const [loadingDocumentTypes, setLoadingDocumentTypes] = useState(true)
  const [steps, setSteps] = useState<StepForm[]>([emptyStep(0)])
  const [workflowStatuses, setWorkflowStatuses] = useState<WorkflowStatus[]>([])
  const [supportedActions, setSupportedActions] = useState<DocumentWorkflowSupportedAction[]>([])
  const [supportedActionsLoaded, setSupportedActionsLoaded] = useState(false)
  const [showVizPreview, setShowVizPreview] = useState(false)
  const [showValidation, setShowValidation] = useState(false)
  // Ref-guard: onemogući re-init form-state-a kada se effect ponovo pokrene (npr. kad
  // stigne supportedActions ili parent proslijedi novu `onError` lambdu) — inače stale
  // `object` prop prepisuje korisnikove izmene.
  const initializedForIdRef = useRef<number | 'create' | null>(null)

  const mapStepFromApi = useCallback(
    (
      s: NonNullable<DocumentWorkflowDefinition['steps']>[number],
      stepsSorted: NonNullable<DocumentWorkflowDefinition['steps']>,
      actions: DocumentWorkflowSupportedAction[]
    ): StepForm => ({
      workflow_status_id: s.workflow_status_id,
      name: s.name ?? '',
      position: s.position,
      assigned_user_ids: s.assigned_user_ids ?? [],
      assigned_group_ids: s.assigned_group_ids ?? [],
      assigned_dynamic_recipients: filterDynamicRecipients(s.assigned_dynamic_recipients),
      notification_delivery: (s.notification_delivery as NotificationDelivery) ?? 'inherit',
      actions: (s.actions ?? []).length ? (s.actions ?? []).map((a) => actionFromApi(a, stepsSorted, actions)) : [emptyAction()],
      entry_conditions: [...(s.entry_conditions ?? [])]
        .sort((a, b) => a.position - b.position)
        .map((c) => ({
          condition_query: c.condition_query || '',
          conditionQueryModel: parseConditionQueryToModel(c.condition_query || ''),
        })),
    }),
    []
  )

  useEffect(() => {
    let cancelled = false
    documentWorkflowSupportedActionService
      .listAll()
      .then((actions) => {
        if (cancelled) return
        setSupportedActions(actions)
        setSupportedActionsLoaded(true)
      })
      .catch(() => {
        if (cancelled) return
        setSupportedActions([])
        setSupportedActionsLoaded(true)
      })
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    documentTypeService
      .listAll()
      .then((r) => setDocumentTypes(r.results || []))
      .catch(() => setDocumentTypes([]))
      .finally(() => setLoadingDocumentTypes(false))
  }, [])

  // Inicijalizacija form-state-a iz `object` propa/API-ja — striktno jednom po `object.id`.
  useEffect(() => {
    const currentKey: number | 'create' = object?.id ?? 'create'
    if (initializedForIdRef.current === currentKey) return

    if (currentKey === 'create') {
      initializedForIdRef.current = 'create'
      return
    }

    // supportedActions su nužni za tačno mapiranje `action_type` po akcijama.
    if (!supportedActionsLoaded) return

    initializedForIdRef.current = currentKey

    const populate = (full: DocumentWorkflowDefinition) => {
      setName(full.name)
      setDescription(full.description || '')
      setApplicableDocumentTypeIds(full.applicable_document_type_ids ?? [])
      const rawSteps = full.steps ?? []
      if (rawSteps.length) {
        const stepsSorted = [...rawSteps].sort((a, b) => a.position - b.position)
        setSteps(stepsSorted.map((s) => mapStepFromApi(s, stepsSorted, supportedActions)))
      }
      setLoadDetail(false)
    }

    if (object?.steps?.length) {
      populate(object)
      return
    }

    documentWorkflowDefinitionService
      .get(currentKey)
      .then(populate)
      .catch((err) => {
        initializedForIdRef.current = null
        onError(err)
      })
  }, [object, supportedActionsLoaded, supportedActions, mapStepFromApi, onError])

  useEffect(() => {
    workflowStatusService.listAll().then((r) => setWorkflowStatuses(r.results || []))
  }, [])

  // Pri kreiranju: kada se učitaju statusi, postavi prvi kao podrazumevan za korake bez izbora
  useEffect(() => {
    if (!isCreating || !workflowStatuses.length) return
    setSteps((prev) => {
      const firstId = workflowStatuses[0].id
      const hasEmpty = prev.some((s) => s.workflow_status_id === '')
      if (!hasEmpty) return prev
      return prev.map((s) => (s.workflow_status_id === '' ? { ...s, workflow_status_id: firstId } : s))
    })
  }, [isCreating, workflowStatuses])

  const setStep = (index: number, update: Partial<StepForm>) => {
    setSteps((prev) => {
      const next = [...prev]
      next[index] = { ...next[index], ...update }
      return next
    })
  }

  const setAction = (stepIndex: number, actionIndex: number, update: Partial<ActionForm>) => {
    setSteps((prev) => {
      const next = [...prev]
      const actions = [...(next[stepIndex].actions || [])]
      actions[actionIndex] = { ...actions[actionIndex], ...update }
      next[stepIndex] = { ...next[stepIndex], actions }
      return next
    })
  }

  const addStep = () => {
    const defaultStatusId = workflowStatuses.length ? workflowStatuses[0].id : undefined
    setSteps((prev) => [...prev, emptyStep(prev.length, defaultStatusId)])
  }

  const removeStep = (index: number) => {
    if (steps.length <= 1) return
    setSteps((prev) => prev.filter((_, i) => i !== index).map((s, idx) => ({ ...s, position: idx })))
  }

  const addAction = (stepIndex: number) => {
    setSteps((prev) => {
      const next = [...prev]
      next[stepIndex] = {
        ...next[stepIndex],
        actions: [...next[stepIndex].actions, emptyAction()],
      }
      return next
    })
  }

  const removeAction = (stepIndex: number, actionIndex: number) => {
    setSteps((prev) => {
      const next = [...prev]
      const actions = next[stepIndex].actions.filter((_, i) => i !== actionIndex)
      next[stepIndex] = {
        ...next[stepIndex],
        actions: actions.length ? actions : [emptyAction()],
      }
      return next
    })
  }

  const setEntryCondition = (stepIndex: number, conditionIndex: number, update: Partial<EntryConditionForm>) => {
    setSteps((prev) => {
      const next = [...prev]
      const conditions = [...(next[stepIndex].entry_conditions || [])]
      conditions[conditionIndex] = { ...conditions[conditionIndex], ...update }
      next[stepIndex] = { ...next[stepIndex], entry_conditions: conditions }
      return next
    })
  }

  const addEntryCondition = (stepIndex: number) => {
    setSteps((prev) => {
      const next = [...prev]
      next[stepIndex] = {
        ...next[stepIndex],
        entry_conditions: [...(next[stepIndex].entry_conditions || []), emptyEntryCondition()],
      }
      return next
    })
  }

  const removeEntryCondition = (stepIndex: number, conditionIndex: number) => {
    setSteps((prev) => {
      const next = [...prev]
      next[stepIndex] = {
        ...next[stepIndex],
        entry_conditions: (next[stepIndex].entry_conditions || []).filter((_, i) => i !== conditionIndex),
      }
      return next
    })
  }

  const setBranch = (
    stepIndex: number,
    actionIndex: number,
    branchIndex: number,
    update: Partial<BranchForm>,
  ) => {
    setSteps((prev) => {
      const next = [...prev]
      const actions = [...(next[stepIndex].actions || [])]
      const branches = [...(actions[actionIndex].branches || [])]
      branches[branchIndex] = { ...branches[branchIndex], ...update }
      actions[actionIndex] = { ...actions[actionIndex], branches }
      next[stepIndex] = { ...next[stepIndex], actions }
      return next
    })
  }

  const addBranch = (stepIndex: number, actionIndex: number) => {
    setSteps((prev) => {
      const next = [...prev]
      const actions = [...(next[stepIndex].actions || [])]
      actions[actionIndex] = {
        ...actions[actionIndex],
        is_conditional: true,
        branches: [...(actions[actionIndex].branches || []), emptyBranch()],
      }
      next[stepIndex] = { ...next[stepIndex], actions }
      return next
    })
  }

  const removeBranch = (stepIndex: number, actionIndex: number, branchIndex: number) => {
    setSteps((prev) => {
      const next = [...prev]
      const actions = [...(next[stepIndex].actions || [])]
      const branches = (actions[actionIndex].branches || []).filter((_, i) => i !== branchIndex)
      actions[actionIndex] = { ...actions[actionIndex], branches }
      next[stepIndex] = { ...next[stepIndex], actions }
      return next
    })
  }

  const toggleActionConditional = (stepIndex: number, actionIndex: number, on: boolean) => {
    setSteps((prev) => {
      const next = [...prev]
      const actions = [...(next[stepIndex].actions || [])]
      const cur = actions[actionIndex]
      actions[actionIndex] = {
        ...cur,
        is_conditional: on,
        branches: on && (!cur.branches || cur.branches.length === 0) ? [emptyBranch()] : (cur.branches || []),
      }
      next[stepIndex] = { ...next[stepIndex], actions }
      return next
    })
  }

  const allowedCustomFieldIds = useMemo(() => {
    if (!applicableDocumentTypeIds.length) return undefined
    const ids = new Set<number>()
    documentTypes
      .filter((dt) => applicableDocumentTypeIds.includes(dt.id))
      .forEach((dt) => {
        ;(dt.custom_fields ?? []).forEach((fieldId) => ids.add(fieldId))
      })
    return ids.size ? Array.from(ids) : undefined
  }, [applicableDocumentTypeIds, documentTypes])

  const buildPayload = useCallback((): DocumentWorkflowDefinitionWrite => {
    const stepsWithStatus = steps.map((s, formIdx) => ({ s, formIdx })).filter(({ s }) => s.workflow_status_id !== '')
    const payloadFormIndices = stepsWithStatus.map(({ formIdx }) => formIdx)
    const formIndexToPayloadIndex = (formIndex: number) => payloadFormIndices.indexOf(formIndex)

    const stepsPayload: DocumentWorkflowDefinitionStepWrite[] = stepsWithStatus.map(({ s }, stepIdx) => {
      const actionsPayload: DocumentWorkflowDefinitionStepActionWrite[] = (s.actions || [])
        .filter((a) => a.name.trim())
        .map((a) => {
          const out: DocumentWorkflowDefinitionStepActionWrite = {
            name: a.name.trim(),
            end_workflow: a.outcome_type === 'end_workflow',
            comment_required: a.comment_required ?? false,
          }
          if (a.outcome_type === 'next_step' && a.target_step_index != null && a.target_step_index >= 0) {
            const payloadIdx = formIndexToPayloadIndex(a.target_step_index)
            if (payloadIdx >= 0) out.next_step_index = payloadIdx
            else out.end_workflow = true
          } else if (a.outcome_type === 'back_to_step' && a.target_step_index != null && a.target_step_index >= 0) {
            const payloadIdx = formIndexToPayloadIndex(a.target_step_index)
            if (payloadIdx >= 0) out.back_to_step_index = payloadIdx
            else out.end_workflow = true
          } else if (a.outcome_type !== 'end_workflow') {
            out.end_workflow = true
          }
          if (a.outcome_type === 'end_workflow' && a.assign_workflow_status_id !== '' && typeof a.assign_workflow_status_id === 'number') {
            out.assign_workflow_status_id = a.assign_workflow_status_id
          }
          if (a.is_conditional) {
            const branchesPayload: DocumentWorkflowDefinitionStepActionBranchWrite[] = (a.branches || [])
              .filter((b) => (b.condition_query || '').trim())
              .map((b, bIdx) => {
                const branchOut: DocumentWorkflowDefinitionStepActionBranchWrite = {
                  position: bIdx,
                  condition_query: b.condition_query,
                }
                if (b.outcome_type === 'end_workflow') {
                  branchOut.end_workflow = true
                  if (b.assign_workflow_status_id !== '' && typeof b.assign_workflow_status_id === 'number') {
                    branchOut.assign_workflow_status_id = b.assign_workflow_status_id
                  }
                } else if (b.outcome_type === 'next_step' && b.target_step_index != null) {
                  const payloadIdx = formIndexToPayloadIndex(b.target_step_index)
                  if (payloadIdx >= 0) branchOut.next_step_index = payloadIdx
                  else branchOut.end_workflow = true
                } else if (b.outcome_type === 'back_to_step' && b.target_step_index != null) {
                  const payloadIdx = formIndexToPayloadIndex(b.target_step_index)
                  if (payloadIdx >= 0) branchOut.back_to_step_index = payloadIdx
                  else branchOut.end_workflow = true
                } else {
                  branchOut.end_workflow = true
                }
                return branchOut
              })
            if (branchesPayload.length) out.branches = branchesPayload
          }
          return out
        })

      const entryConditionsPayload: DocumentWorkflowDefinitionStepEntryConditionWrite[] = (s.entry_conditions || [])
        .filter((c) => (c.condition_query || '').trim())
        .map((c, idx) => ({ position: idx, condition_query: c.condition_query }))

      return {
        workflow_status_id: s.workflow_status_id as number,
        name: (s.name ?? '').trim() || undefined,
        position: stepIdx,
        assigned_user_ids: s.assigned_user_ids?.length ? s.assigned_user_ids : undefined,
        assigned_group_ids: s.assigned_group_ids?.length ? s.assigned_group_ids : undefined,
        assigned_dynamic_recipients: s.assigned_dynamic_recipients?.length ? s.assigned_dynamic_recipients : undefined,
        notification_delivery: (s.notification_delivery ?? 'inherit') as NotificationDelivery,
        actions: actionsPayload.length ? actionsPayload : undefined,
        entry_conditions: entryConditionsPayload.length ? entryConditionsPayload : undefined,
      }
    })
    return {
      name: name.trim(),
      slug: slugFromName(name).trim() || 'workflow',
      description: (description || '').trim(),
      applicable_document_type_ids: applicableDocumentTypeIds,
      steps: stepsPayload,
    }
  }, [name, description, steps, applicableDocumentTypeIds])

  const getErrorMessage = (err: unknown): string => {
    const e = err as { response?: { data?: { detail?: string; [key: string]: unknown } } }
    const d = e?.response?.data
    if (!d) return (err as Error)?.message || String(err)
    if (typeof d.detail === 'string') return d.detail
    if (typeof d.detail === 'object' && d.detail !== null) return JSON.stringify(d.detail)
    const fieldErrors = Object.entries(d)
      .filter(([k]) => k !== 'detail')
      .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`)
    if (fieldErrors.length) return fieldErrors.join('; ')
    return JSON.stringify(d)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setShowValidation(false)
    if (!name.trim()) {
      setShowValidation(true)
      onError(new Error(t('name') + ' ' + t('required')))
      return
    }
    const payload = buildPayload()
    if (!payload.steps?.length) {
      setShowValidation(true)
      onError(new Error(t('document_workflow.at_least_one_step')))
      return
    }
    setLoading(true)
    try {
      if (isCreating) {
        await documentWorkflowDefinitionService.create(payload)
      } else if (object?.id) {
        await documentWorkflowDefinitionService.update(object.id, payload)
      }
      onSuccess()
    } catch (err) {
      onError(new Error(getErrorMessage(err)))
    } finally {
      setLoading(false)
    }
  }

  const allStepOptions = steps.map((s, idx) => ({
    value: idx,
    label: s.name && s.name.trim() ? s.name.trim() : `${t('document_workflow.step')} ${idx + 1}`,
  }))

  const vizPreviewDefinition: DocumentWorkflowDefinition | null = useMemo(() => {
    if (!steps.length) return null
    const wsId = (id: number | '') => (typeof id === 'number' ? id : 0)
    return {
      id: 0,
      name: name.trim() || t('document_workflow.workflow_definitions_title'),
      slug: slugFromName(name).trim() || 'preview',
      description: description.trim(),
      enabled: true,
      steps: steps.map((s, i) => ({
        id: i,
        workflow_status_id: wsId(s.workflow_status_id),
        name: s.name || undefined,
        position: i,
        assigned_user_ids: s.assigned_user_ids,
        assigned_dynamic_recipients: s.assigned_dynamic_recipients,
        actions: (s.actions || []).map((a) => ({
          name: a.name || '',
          end_workflow: a.outcome_type === 'end_workflow',
          next_step_id: a.outcome_type === 'next_step' && a.target_step_index != null ? a.target_step_index : null,
          back_to_step_id: a.outcome_type === 'back_to_step' && a.target_step_index != null ? a.target_step_index : null,
          assign_workflow_status_id: a.assign_workflow_status_id !== '' ? a.assign_workflow_status_id : null,
          branches: a.is_conditional
            ? (a.branches || []).map((b, bIdx) => ({
                position: bIdx,
                condition_query: b.condition_query,
                end_workflow: b.outcome_type === 'end_workflow',
                next_step_id: b.outcome_type === 'next_step' && b.target_step_index != null ? b.target_step_index : null,
                back_to_step_id: b.outcome_type === 'back_to_step' && b.target_step_index != null ? b.target_step_index : null,
                assign_workflow_status_id: b.assign_workflow_status_id !== '' ? b.assign_workflow_status_id : null,
              }))
            : [],
        })),
      })),
    }
  }, [name, description, steps, t])

  const vizPreviewStatusMap = useMemo(() => {
    const map: Record<number, string> = {}
    workflowStatuses.forEach((ws) => {
      map[ws.id] = ws.name
    })
    return map
  }, [workflowStatuses])

  if (loadDetail) {
    return (
      <Modal show onHide={onClose} size="lg" centered>
        <Modal.Body className="text-center py-5">
          <div className="spinner-border text-primary" />
          <p className="mt-2 text-muted">{t('loading_data')}</p>
        </Modal.Body>
      </Modal>
    )
  }

  return (
    <Modal show onHide={onClose} size="xl" centered scrollable>
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>{isCreating ? t('document_workflow.create_definition') : t('document_workflow.edit_definition')}</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          <Form.Group className="mb-3">
            <Form.Label>{t('name')} *</Form.Label>
            <Form.Control value={name} onChange={(e) => setName(e.target.value)} placeholder={t('name')} required />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>{t('document_workflow.description')}</Form.Label>
            <Form.Control as="textarea" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>
              {t('document_workflow.applicable_document_types')}
              <InfoTip text={t('document_workflow.applicable_document_types_help')} />
            </Form.Label>
            {documentTypes.length === 0 && !loadingDocumentTypes ? (
              <p className="text-muted small mb-0">{t('document_workflow.no_applicable_document_types')}</p>
            ) : (
              <Select
                isMulti
                isLoading={loadingDocumentTypes}
                isClearable
                classNamePrefix="react-select"
                className="react-select-container"
                placeholder={t('document_workflow.applicable_document_types_placeholder')}
                noOptionsMessage={() => t('document_workflow.no_applicable_document_types')}
                options={documentTypes.map((dt) => ({ value: dt.id, label: dt.name }))}
                value={applicableDocumentTypeIds
                  .map((id) => {
                    const dt = documentTypes.find((d) => d.id === id)
                    return dt ? { value: dt.id, label: dt.name } : { value: id, label: `#${id}` }
                  })}
                onChange={(selected) => {
                  const arr = selected as ReadonlyArray<{ value: number }> | null
                  setApplicableDocumentTypeIds(arr ? arr.map((o) => o.value) : [])
                }}
              />
            )}
          </Form.Group>

          <hr />
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h6 className="mb-0">{t('document_workflow.steps')}</h6>
            <Button type="button" variant="outline-secondary" size="sm" onClick={addStep}>
              <i className="bi bi-plus" /> {t('document_workflow.add_step')}
            </Button>
          </div>

          {steps.map((step, stepIdx) => (
            <div key={stepIdx} className="border rounded p-3 mb-3 bg-light">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <strong>{step.name && step.name.trim() ? step.name.trim() : `${t('document_workflow.step')} ${stepIdx + 1}`}</strong>{' '}
                <Form.Group className="mb-2">
                  <Form.Label className="small">{t('document_workflow.step_name')}</Form.Label>
                  <Form.Control
                    size="sm"
                    value={step.name}
                    onChange={(e) => setStep(stepIdx, { name: e.target.value })}
                    placeholder={t('document_workflow.step_name_placeholder')}
                  />
                  <Form.Text className="text-muted small">{t('document_workflow.step_name_help')}</Form.Text>
                </Form.Group>
                {steps.length > 1 && (
                  <Button type="button" variant="outline-danger" size="sm" onClick={() => removeStep(stepIdx)}>
                    <i className="bi bi-trash" />
                  </Button>
                )}
              </div>

              <Form.Group className="mb-2">
                <Form.Label className="small">
                  {t('document_workflow.current_status')} / {t('document_workflow.workflow_status')}{' '}
                  <span className="text-danger" title={t('required')}>
                    *
                  </span>
                </Form.Label>
                <Form.Select
                  value={step.workflow_status_id === '' ? '' : step.workflow_status_id}
                  onChange={(e) => {
                    setStep(stepIdx, { workflow_status_id: e.target.value ? parseInt(e.target.value, 10) : '' })
                    setShowValidation(false)
                  }}
                  size="sm"
                  isInvalid={showValidation && step.workflow_status_id === ''}
                >
                  {workflowStatuses.map((ws) => (
                    <option key={ws.id} value={ws.id}>
                      {ws.name}
                    </option>
                  ))}
                </Form.Select>
                {showValidation && step.workflow_status_id === '' && (
                  <Form.Control.Feedback type="invalid">{t('document_workflow.workflow_status_required_hint')}</Form.Control.Feedback>
                )}
              </Form.Group>

              <Form.Group className="mb-2">
                <Form.Label className="small d-flex justify-content-between align-items-center">
                  <span>
                    {t('document_workflow.step_entry_conditions')}
                    <InfoTip text={t('document_workflow.step_entry_conditions_help')} />
                  </span>
                  <Button
                    type="button"
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => addEntryCondition(stepIdx)}
                  >
                    <i className="bi bi-plus" /> {t('document_workflow.step_entry_conditions_add')}
                  </Button>
                </Form.Label>
                {(step.entry_conditions?.length ?? 0) === 0 ? (
                  <p className="text-muted small mb-0">{t('document_workflow.step_entry_conditions_empty_short')}</p>
                ) : (
                  <div className="d-flex flex-column gap-2">
                    {step.entry_conditions.map((condition, conditionIdx) => (
                      <div key={conditionIdx} className="d-flex align-items-start gap-2 border rounded p-2 bg-white">
                        <div className="flex-grow-1" style={{ minWidth: 240 }}>
                          <CustomFieldsQueryDropdown
                            selectionModel={condition.conditionQueryModel}
                            onSelectionModelChange={(updatedModel) => {
                              const serialized = updatedModel.serialize()
                              setEntryCondition(stepIdx, conditionIdx, {
                                conditionQueryModel: updatedModel,
                                condition_query: serialized,
                              })
                            }}
                            useDropdown={false}
                            allowedCustomFieldIds={allowedCustomFieldIds}
                          />
                        </div>
                        <Button
                          type="button"
                          variant="outline-danger"
                          size="sm"
                          onClick={() => removeEntryCondition(stepIdx, conditionIdx)}
                        >
                          <i className="bi bi-x" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </Form.Group>

              <Form.Group className="mb-2">
                <Form.Label className="small">{t('document_workflow.assign_view')}</Form.Label>
                <div className="row">
                  <div className="col-md-6">
                    <InputWorkflowStepAssignees
                      userIds={step.assigned_user_ids}
                      dynamicRecipients={step.assigned_dynamic_recipients}
                      onChange={(ids, dynamic) =>
                        setStep(stepIdx, { assigned_user_ids: ids, assigned_dynamic_recipients: dynamic })
                      }
                    />
                  </div>
                  <div className="col-md-6">
                    <InputPermissionsGroup value={step.assigned_group_ids} onChange={(ids) => setStep(stepIdx, { assigned_group_ids: ids })} />
                  </div>
                </div>
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label className="small">{t('document_workflow.notification_delivery')}</Form.Label>
                <Form.Select
                  size="sm"
                  value={step.notification_delivery ?? 'inherit'}
                  onChange={(e) => setStep(stepIdx, { notification_delivery: e.target.value as NotificationDelivery })}
                >
                  <option value="inherit">{t('document_workflow.notification_delivery_inherit')}</option>
                  <option value="websocket_only">{t('document_workflow.notification_delivery_ws_only')}</option>
                  <option value="email_only">{t('document_workflow.notification_delivery_email_only')}</option>
                  <option value="websocket_and_email">{t('document_workflow.notification_delivery_ws_and_email')}</option>
                </Form.Select>
                <Form.Text className="text-muted small">{t('document_workflow.notification_delivery_help')}</Form.Text>
              </Form.Group>

              <Form.Label className="small mb-2 d-block">{t('document_workflow.actions')}</Form.Label>
              {(step.actions || []).map((action, actionIdx) => (
                <div key={actionIdx} className="workflow-definition-action">
                  <div className="workflow-definition-action__main d-flex flex-wrap align-items-center gap-2">
                    <Form.Select
                      size="sm"
                      value={action.action_type}
                      onChange={(e) => {
                        const value = e.target.value
                        const defaultLabel = getDefaultLabelForActionType(value, supportedActions)
                        setAction(stepIdx, actionIdx, {
                          action_type: value,
                          name: defaultLabel || action.name,
                        })
                      }}
                      className="workflow-definition-action__type"
                    >
                      {supportedActions.map((a) => (
                        <option key={a.id} value={a.slug}>
                          {a.default_label}
                        </option>
                      ))}
                      <option value="custom">{t('document_workflow.action_type_custom')}</option>
                    </Form.Select>
                    <Form.Control
                      size="sm"
                      placeholder={
                        action.action_type === 'custom'
                          ? t('document_workflow.action_name_placeholder')
                          : t('document_workflow.action_custom_label_placeholder')
                      }
                      value={action.name}
                      onChange={(e) => setAction(stepIdx, actionIdx, { name: e.target.value })}
                      className="workflow-definition-action__label"
                    />
                    <span className="text-muted small flex-shrink-0">{t('document_workflow.action_then')}:</span>
                    <Form.Select
                      size="sm"
                      value={action.outcome_type}
                      onChange={(e) =>
                        setAction(stepIdx, actionIdx, {
                          outcome_type: e.target.value as ActionOutcomeType,
                          target_step_index: e.target.value === 'end_workflow' ? null : action.target_step_index,
                        })
                      }
                      className="workflow-definition-action__outcome"
                    >
                      <option value="next_step">{t('document_workflow.action_outcome_next')}</option>
                      <option value="back_to_step">{t('document_workflow.action_outcome_back')}</option>
                      <option value="end_workflow">{t('document_workflow.action_outcome_end')}</option>
                    </Form.Select>
                    {action.outcome_type === 'end_workflow' && (
                      <Form.Select
                        size="sm"
                        value={action.assign_workflow_status_id === '' ? '' : action.assign_workflow_status_id}
                        onChange={(e) =>
                          setAction(stepIdx, actionIdx, {
                            assign_workflow_status_id: e.target.value === '' ? '' : parseInt(e.target.value, 10),
                          })
                        }
                        className="workflow-definition-action__target"
                        title={t('document_workflow.assign_status_on_close_workflow')}
                      >
                        <option value="">{t('document_workflow.assign_status_on_close_optional')}</option>
                        {workflowStatuses.map((ws) => (
                          <option key={ws.id} value={ws.id}>
                            {ws.name}
                          </option>
                        ))}
                      </Form.Select>
                    )}
                    {action.outcome_type === 'next_step' && (
                      <Form.Select
                        size="sm"
                        value={action.target_step_index ?? ''}
                        onChange={(e) =>
                          setAction(stepIdx, actionIdx, {
                            target_step_index: e.target.value === '' ? null : parseInt(e.target.value, 10),
                          })
                        }
                        className="workflow-definition-action__target"
                      >
                        <option value="">—</option>
                        {allStepOptions
                          .filter((o) => o.value > stepIdx)
                          .map((o) => (
                            <option key={o.value} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                      </Form.Select>
                    )}
                    {action.outcome_type === 'back_to_step' && (
                      <Form.Select
                        size="sm"
                        value={action.target_step_index ?? ''}
                        onChange={(e) =>
                          setAction(stepIdx, actionIdx, {
                            target_step_index: e.target.value === '' ? null : parseInt(e.target.value, 10),
                          })
                        }
                        className="workflow-definition-action__target"
                      >
                        <option value="">—</option>
                        {allStepOptions
                          .filter((o) => o.value < stepIdx)
                          .map((o) => (
                            <option key={o.value} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                      </Form.Select>
                    )}
                    {(step.actions?.length ?? 0) > 1 && (
                      <Button type="button" variant="outline-danger" size="sm" onClick={() => removeAction(stepIdx, actionIdx)}>
                        <i className="bi bi-x" />
                      </Button>
                    )}
                  </div>
                  <div className="workflow-definition-action__options">
                    <Form.Check
                      type="switch"
                      id={`conditional-${stepIdx}-${actionIdx}`}
                      label={
                        <>
                          {t('document_workflow.action_conditional')}
                          <InfoTip text={t('document_workflow.action_conditional_help')} />
                        </>
                      }
                      checked={action.is_conditional}
                      onChange={(e) => toggleActionConditional(stepIdx, actionIdx, e.target.checked)}
                      className="workflow-definition-action__switch mb-0"
                    />
                    {action.is_conditional && (
                      <div className="workflow-definition-action__branches mt-2">
                        {steps.length <= 1 && (
                          <div className="alert alert-warning py-2 px-3 small mb-2">
                            <i className="bi bi-exclamation-triangle me-1" />
                            {t('document_workflow.action_branch_no_targets_hint')}
                          </div>
                        )}
                        {(action.branches || []).map((branch, branchIdx) => (
                          <div key={branchIdx} className="workflow-definition-action__branch border rounded p-2 mb-2 bg-white">
                            <div className="d-flex align-items-start gap-2 mb-2">
                              <span
                                className="badge bg-dark align-self-start mt-1"
                                title={t('document_workflow.action_branches_order_hint')}
                              >
                                {branchIdx + 1}
                              </span>
                              <div className="flex-grow-1" style={{ minWidth: 240 }}>
                                <CustomFieldsQueryDropdown
                                  selectionModel={branch.conditionQueryModel}
                                  onSelectionModelChange={(updatedModel) => {
                                    setBranch(stepIdx, actionIdx, branchIdx, {
                                      conditionQueryModel: updatedModel,
                                      condition_query: updatedModel.serialize(),
                                    })
                                  }}
                                  useDropdown={false}
                                  allowedCustomFieldIds={allowedCustomFieldIds}
                                />
                              </div>
                              <Button
                                type="button"
                                variant="outline-danger"
                                size="sm"
                                onClick={() => removeBranch(stepIdx, actionIdx, branchIdx)}
                                title={t('document_workflow.action_branch_remove')}
                              >
                                <i className="bi bi-x" />
                              </Button>
                            </div>
                            <div className="d-flex flex-nowrap align-items-center gap-2 ms-4 ps-2">
                              <span className="text-muted small flex-shrink-0">
                                {t('document_workflow.action_branch_then')}:
                              </span>
                              <Form.Select
                                size="sm"
                                value={
                                  branch.outcome_type === 'end_workflow'
                                    ? 'end'
                                    : branch.target_step_index != null
                                    ? `step:${branch.target_step_index}`
                                    : ''
                                }
                                onChange={(e) => {
                                  const v = e.target.value
                                  if (v === 'end') {
                                    setBranch(stepIdx, actionIdx, branchIdx, {
                                      outcome_type: 'end_workflow',
                                      target_step_index: null,
                                    })
                                  } else if (v.startsWith('step:')) {
                                    const idx = parseInt(v.slice('step:'.length), 10)
                                    setBranch(stepIdx, actionIdx, branchIdx, {
                                      outcome_type: idx > stepIdx ? 'next_step' : 'back_to_step',
                                      target_step_index: idx,
                                    })
                                  } else {
                                    setBranch(stepIdx, actionIdx, branchIdx, {
                                      outcome_type: 'next_step',
                                      target_step_index: null,
                                    })
                                  }
                                }}
                                style={{ minWidth: 220 }}
                              >
                                <option value="">
                                  {t('document_workflow.action_branch_then_placeholder')}
                                </option>
                                {allStepOptions
                                  .filter((o) => o.value !== stepIdx)
                                  .map((o) => (
                                    <option key={`step:${o.value}`} value={`step:${o.value}`}>
                                      {o.label}
                                    </option>
                                  ))}
                                <option value="end">{t('document_workflow.action_outcome_end')}</option>
                              </Form.Select>
                              {branch.outcome_type === 'end_workflow' && (
                                <Form.Select
                                  size="sm"
                                  value={branch.assign_workflow_status_id === '' ? '' : branch.assign_workflow_status_id}
                                  onChange={(e) =>
                                    setBranch(stepIdx, actionIdx, branchIdx, {
                                      assign_workflow_status_id: e.target.value === '' ? '' : parseInt(e.target.value, 10),
                                    })
                                  }
                                  title={t('document_workflow.assign_status_on_close_workflow')}
                                >
                                  <option value="">{t('document_workflow.assign_status_on_close_optional')}</option>
                                  {workflowStatuses.map((ws) => (
                                    <option key={ws.id} value={ws.id}>
                                      {ws.name}
                                    </option>
                                  ))}
                                </Form.Select>
                              )}
                            </div>
                          </div>
                        ))}
                        <Button
                          type="button"
                          variant="outline-primary"
                          size="sm"
                          onClick={() => addBranch(stepIdx, actionIdx)}
                        >
                          <i className="bi bi-plus" /> {t('document_workflow.action_branch_add')}
                        </Button>
                        <div className="workflow-definition-action__branch-fallback mt-3 p-2 rounded bg-white border">
                          <span className="badge bg-warning text-dark me-2">
                            {t('document_workflow.action_branch_otherwise')}
                          </span>
                          <span className="text-muted small">
                            {t('document_workflow.action_branch_otherwise_hint')}
                          </span>
                        </div>
                      </div>
                    )}
                    <Form.Check
                      type="switch"
                      id={`comment-required-${stepIdx}-${actionIdx}`}
                      label={t('document_workflow.action_comment_required')}
                      checked={action.comment_required ?? false}
                      onChange={(e) => setAction(stepIdx, actionIdx, { comment_required: e.target.checked })}
                      className="workflow-definition-action__switch mb-0 mt-2"
                    />
                    {action.comment_required && (
                      <Form.Text className="text-muted small d-block mt-1 ms-4 ps-2">
                        {t('document_workflow.action_comment_required_help')}
                      </Form.Text>
                    )}
                  </div>
                </div>
              ))}
              <Button
                type="button"
                variant="outline-secondary"
                size="sm"
                className="workflow-definition-action__add"
                onClick={() => addAction(stepIdx)}
              >
                <i className="bi bi-plus" /> {t('document_workflow.add_action')}
              </Button>
            </div>
          ))}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onClose}>
            {t('cancel')}
          </Button>
          <Button variant="primary" type="submit" disabled={loading}>
            {loading ? <span className="spinner-border spinner-border-sm me-1" /> : null}
            {t('save')}
          </Button>
        </Modal.Footer>
      </Form>

      {showVizPreview && vizPreviewDefinition && (
        <DocumentWorkflowDefinitionVisualizationDialog
          definitionName={vizPreviewDefinition.name}
          initialDefinition={vizPreviewDefinition}
          initialStatusMap={vizPreviewStatusMap}
          onClose={() => setShowVizPreview(false)}
        />
      )}
    </Modal>
  )
}
